import React, { FC } from 'react';
import moment from 'moment';
import { v4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { AlarmLogEventKind } from '@x-guard/xgac-types/xgac';
import { alarmIdSelector } from 'features/details';
import { ackedSelector } from 'features/details/ack/exports';
import {
  appendOptimisticOperator,
  removeOptimisticOperator,
  stateSelector,
} from 'features/details/chat';
import { api, useLocale } from 'lib';
import { ErrorDisplay, WithAsync } from 'components';
import { userContextSelector } from 'features/identity/auth/exports';
import { AsyncStatus } from 'types';
import { ChatInputField } from './messages/input';
import { Timeline } from './timeline';
import styles from './style.module.scss';

export const Chat: FC = () => {

  const { t } = useLocale();
  const dispatch = useDispatch();
  const alarmId = useSelector(alarmIdSelector);
  const userContext = useSelector(userContextSelector);
  const state = useSelector(stateSelector);
  const acked = useSelector(ackedSelector);

  const sendMessage = (text: string) => {

    const createdAt = moment.utc().toISOString();

    const optimisticId = `local-${v4()}`;

    dispatch(appendOptimisticOperator({
      _id: optimisticId,
      message: text,
      createdAt,
      name: userContext.user.name,
    }));

    api.post(`/alarms/${alarmId}/alarm-log-events`, {
      data: {
        eventType: AlarmLogEventKind.OperatorChat,
        message: text,
      },
    }).catch(() => {

      dispatch(removeOptimisticOperator(optimisticId));

    });

  };

  return (
    <div className={styles.chatBlock}>
      <div className={styles.blockTitleContainer}>
        {t('Alarm_Detail_Chat_Title')}
      </div>
      <WithAsync
        status={state.status}
        error={() => (
          <ErrorDisplay
            isRetrying={false}
            {...(state.error ?? { message: t('Alarm_Detail_Logs_Unknown') })}
          />
        )}
      >
        {() => (
          <>
            {state.status === AsyncStatus.Fulfilled && (
              <Timeline
                callEvents={state.callEvents}
                participants={state.participants}
                operatorMessages={state.operatorMessages}
                optimisticOperator={state.optimisticOperator}
                logs={state.logs}
              />
            )}
            {!acked && <p className={styles.notice}>{t('Alarm_Detail_Chat_OperatorNotice')}</p>}
            <ChatInputField onSubmit={sendMessage}/>
          </>
        )}
      </WithAsync>
    </div>
  );

};
